import type { FC } from 'react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { useStorageLocationEdit } from 'src/apis/storage/use-edit-storage-location';
import type { PublicMarketAreasRequest } from 'src/apis/storage/use-public-market-areas';
import type { StorageLocationDetailsRequest } from 'src/apis/storage/use-storage-location-details';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Form } from 'src/components/form/form';
import { PageHeader } from 'src/components/layout/page-header';
import type { Successful } from 'src/hooks/use-axios';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { CreateStorageLocationFormFields } from 'src/pages/storage/locations/create/fields';
import {
  formValuesToCreateRequest,
  getCreateStorageLocationSchema,
} from 'src/pages/storage/locations/create/schema';
import { Forbidden } from 'src/utils/errors';

type Props = {
  storageLocationDetails: Successful<StorageLocationDetailsRequest>;
  marketAreas: Successful<PublicMarketAreasRequest>;
};

export const StorageLocationEditForm: FC<Props> = ({
  storageLocationDetails,
  marketAreas,
}) => {
  const details = storageLocationDetails.response.data;
  const editLink = details._links.editStorageLocation;

  if (!editLink) {
    throw new Forbidden();
  }

  const title = `Edit ${details.locationName}`;
  useTitle(title);

  const referrer = useReferrer({
    label: details.locationName,
    location: `/storage/locations/${details.locationId}`,
  });

  const editStorageLocation = useStorageLocationEdit();
  const marketAreasList = marketAreas.response.data._embedded.items;

  useEffect(() => {
    if (!editStorageLocation.response) return;
    toast.success('Storage location was successfully edited.');
    referrer.goTo();
    // oxlint-disable-next-line exhaustive-deps
  }, [editStorageLocation.response]);

  return (
    <div
      className={`
        grid items-center gap-xs
        legacy:gap-[1rem]
      `}
    >
      <PageHeader title={title} referrer={referrer} />

      <Card data-testid="edit-storage-location-form">
        <Form
          initialValues={{
            locationName: details.locationName,
            locationEic: details.locationEic ?? null,
            link: details.link ?? null,
            comment: details.comment ?? null,
            virtualPoint: details.virtualPoint ? 'true' : 'false',
            marketAreas: details.marketAreas.map((entry) => ({
              id: uuid(),
              marketAreaId: {
                label: entry.marketArea.name,
                value: entry.marketArea.id,
              },
              injectionPoints: entry.injectionPoints.map((np) => ({
                label: np.name,
                value: np.id,
                hint: `Exit: ${entry.marketArea.name}`,
              })),
              withdrawalPoints: entry.withdrawalPoints.map((np) => ({
                label: np.name,
                value: np.id,
                hint: `Entry: ${entry.marketArea.name}`,
              })),
            })),
          }}
          schema={getCreateStorageLocationSchema}
          onSubmit={(values) => {
            const requestData = formValuesToCreateRequest(values);
            editStorageLocation.execute({
              url: editLink.href,
              data: requestData,
            });
          }}
          constraintViolation={editStorageLocation.error}
          cancelOrResetButton={referrer}
          submitButton={
            <Button
              type="submit"
              disabled={editStorageLocation.pending}
              data-testid="edit-storage-location"
            >
              {editStorageLocation.pending ? 'Pending...' : 'Edit Location'}
            </Button>
          }
        >
          <CreateStorageLocationFormFields marketAreas={marketAreasList} />
        </Form>
      </Card>
    </div>
  );
};
